import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    ZenegyEmployeeTypeEnum,
    ZenegySalaryModeEnum,
    ZenegySalaryTypeEnum
} from '@/models/payment';

interface EmployeeFilters {
    selectedEmployeeTypes: ZenegyEmployeeTypeEnum[];
    selectedPandayEmployeeIds: string[];
    selectedSalaryModes: ZenegySalaryModeEnum[];
    selectedSalaryTypes: ZenegySalaryTypeEnum[];
    selectedDepartments: string[];
    selectedActiveType: string;
    selectedInstapaidSalaryTypes: string[];
    username: string;
}

interface EmployeeFiltersHandlers {
    setSelectedEmployeeTypes: (value: ZenegyEmployeeTypeEnum[]) => void;
    setPandayEmployeeTypesIds: (value: string[]) => void;
    setSelectedSalaryModes: (value: ZenegySalaryModeEnum[]) => void;
    setSelectedSalaryTypes: (value: ZenegySalaryTypeEnum[]) => void;
    setSelectedDepartments: (value: string[]) => void;
    setSelectedActiveType: (value: string) => void;
    setSelectedInstapaidSalaryTypes: (value: string[] | string) => void;
    setUsername: (value: string) => void;
}

interface UseEmployeeFiltersReturn
    extends EmployeeFilters, EmployeeFiltersHandlers {
    filtersKey: string; // Used to detect filter changes for page reset
}

/**
 * Custom hook to manage employee filters via URL search params
 * Provides synchronized state management between URL and component state
 */
export const useEmployeeFilters = (): UseEmployeeFiltersReturn => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Parse array parameter from URL
    const getArrayParam = useCallback(
        (key: string): string[] => {
            const param = searchParams.get(key);
            return param ? param.split(',').filter(Boolean) : [];
        },
        [searchParams]
    );

    // Parse string parameter from URL
    const getStringParam = useCallback(
        (key: string): string => {
            return searchParams.get(key) || '';
        },
        [searchParams]
    );

    // Initialize all filter states from URL
    const [selectedEmployeeTypes, setSelectedEmployeeTypes] = useState<
        ZenegyEmployeeTypeEnum[]
    >(() => getArrayParam('employeeTypes') as ZenegyEmployeeTypeEnum[]);

    const [selectedPandayEmployeeIds, setPandayEmployeeTypesIds] = useState<
        string[]
    >(() => getArrayParam('plandayEmployeeTypes'));

    const [selectedSalaryModes, setSelectedSalaryModes] = useState<
        ZenegySalaryModeEnum[]
    >(() => getArrayParam('salaryModes') as ZenegySalaryModeEnum[]);

    const [selectedSalaryTypes, setSelectedSalaryTypes] = useState<
        ZenegySalaryTypeEnum[]
    >(() => getArrayParam('salaryTypes') as ZenegySalaryTypeEnum[]);

    const [selectedDepartments, setSelectedDepartments] = useState<string[]>(
        () => getArrayParam('departments')
    );

    const [selectedActiveType, setSelectedActiveType] = useState<string>(() =>
        getStringParam('activeType')
    );

    const [selectedInstapaidSalaryTypes, setSelectedInstapaidSalaryTypes] =
        useState<string[]>(() => {
            const param = searchParams.get('instaPaidSalaryType');
            return param ? [param] : [];
        });

    const [username, setUsername] = useState<string>(() =>
        getStringParam('username')
    );

    // Update URL params when filters change
    const updateUrlParam = useCallback(
        (key: string, value: string | string[]) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev);
                    if (Array.isArray(value)) {
                        newParams.delete(key);
                        if (value.length > 0) {
                            newParams.set(key, value.join(','));
                        }
                    } else {
                        if (value) {
                            newParams.set(key, value);
                        } else {
                            newParams.delete(key);
                        }
                    }
                    return newParams;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // Sync state when URL changes (browser navigation, reset, etc.)
    useEffect(() => {
        setSelectedEmployeeTypes(
            getArrayParam('employeeTypes') as ZenegyEmployeeTypeEnum[]
        );
        setPandayEmployeeTypesIds(getArrayParam('plandayEmployeeTypes'));
        setSelectedSalaryModes(
            getArrayParam('salaryModes') as ZenegySalaryModeEnum[]
        );
        setSelectedSalaryTypes(
            getArrayParam('salaryTypes') as ZenegySalaryTypeEnum[]
        );
        setSelectedDepartments(getArrayParam('departments'));
        setSelectedActiveType(getStringParam('activeType'));
        setUsername(getStringParam('username'));

        const instaPaidParam = searchParams.get('instaPaidSalaryType');
        setSelectedInstapaidSalaryTypes(instaPaidParam ? [instaPaidParam] : []);
    }, [searchParams, getArrayParam, getStringParam]);

    // Create a stable key that changes when any filter changes
    // This can be used to trigger page resets
    const filtersKey = useMemo(() => {
        return [
            selectedEmployeeTypes.join(','),
            selectedPandayEmployeeIds.join(','),
            selectedSalaryModes.join(','),
            selectedSalaryTypes.join(','),
            selectedDepartments.join(','),
            selectedActiveType,
            Array.isArray(selectedInstapaidSalaryTypes)
                ? selectedInstapaidSalaryTypes.join(',')
                : selectedInstapaidSalaryTypes,
            username
        ].join('|');
    }, [
        selectedEmployeeTypes,
        selectedPandayEmployeeIds,
        selectedSalaryModes,
        selectedSalaryTypes,
        selectedDepartments,
        selectedActiveType,
        selectedInstapaidSalaryTypes,
        username
    ]);

    // Wrapper setters that also update URL
    const setEmployeeTypes = useCallback(
        (value: ZenegyEmployeeTypeEnum[]) => {
            setSelectedEmployeeTypes(value);
            updateUrlParam('employeeTypes', value);
        },
        [updateUrlParam]
    );

    const setPlandayEmployeeIds = useCallback(
        (value: string[]) => {
            setPandayEmployeeTypesIds(value);
            updateUrlParam('plandayEmployeeTypes', value);
        },
        [updateUrlParam]
    );

    const setSalaryModes = useCallback(
        (value: ZenegySalaryModeEnum[]) => {
            setSelectedSalaryModes(value);
            updateUrlParam('salaryModes', value);
        },
        [updateUrlParam]
    );

    const setSalaryTypes = useCallback(
        (value: ZenegySalaryTypeEnum[]) => {
            setSelectedSalaryTypes(value);
            updateUrlParam('salaryTypes', value);
        },
        [updateUrlParam]
    );

    const setDepartments = useCallback(
        (value: string[]) => {
            setSelectedDepartments(value);
            updateUrlParam('departments', value);
        },
        [updateUrlParam]
    );

    const setActiveType = useCallback(
        (value: string) => {
            setSelectedActiveType(value);
            updateUrlParam('activeType', value);
        },
        [updateUrlParam]
    );

    const setInstapaidSalaryTypes = useCallback(
        (value: string[] | string) => {
            const arrayValue = Array.isArray(value)
                ? value
                : value
                  ? [value]
                  : [];
            setSelectedInstapaidSalaryTypes(arrayValue);
            const singleValue = arrayValue.length > 0 ? arrayValue[0] : '';
            updateUrlParam('instaPaidSalaryType', singleValue);
        },
        [updateUrlParam]
    );

    const setUsernameFilter = useCallback(
        (value: string) => {
            setUsername(value);
            updateUrlParam('username', value);
        },
        [updateUrlParam]
    );

    return {
        // State
        selectedEmployeeTypes,
        selectedPandayEmployeeIds,
        selectedSalaryModes,
        selectedSalaryTypes,
        selectedDepartments,
        selectedActiveType,
        selectedInstapaidSalaryTypes,
        username,
        // Setters
        setSelectedEmployeeTypes: setEmployeeTypes,
        setPandayEmployeeTypesIds: setPlandayEmployeeIds,
        setSelectedSalaryModes: setSalaryModes,
        setSelectedSalaryTypes: setSalaryTypes,
        setSelectedDepartments: setDepartments,
        setSelectedActiveType: setActiveType,
        setSelectedInstapaidSalaryTypes: setInstapaidSalaryTypes,
        setUsername: setUsernameFilter,
        // Key for detecting changes
        filtersKey
    };
};
